import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import WorkstreamBreadcrumb from '../components/WorkstreamBreadcrumb';
import WorkstreamActions from '../components/WorkstreamActions';
import EditableTitle from '../components/EditableTitle';
import EditableDescription from '../components/EditableDescription';
import TaskSettings from '../components/TaskSettings';
import ShowArchivedToggle from '../components/ShowArchivedToggle';
import SwimLanes from '../components/SwimLanes';
import ErrorBanners from '../components/ErrorBanners';
import PermalinkCopyButton from '../components/PermalinkCopyButton';
import type { Workstream } from '../types/workstream';
import type { Task, ListTasksResponse } from '../types/task';
import useTaskPolling from '../hooks/useTaskPolling';

export default function WorkstreamViewPage() {
    const { workstreamID } = useParams<{ workstreamID: string }>();
    const [workstream, setWorkstream] = useState<Workstream | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | undefined>();
    const activeIDRef = useRef(workstreamID);
    const [showArchived, setShowArchivedState] = useState<boolean>(() => {
        try {
            return localStorage.getItem('workstream.showArchived') === 'true';
        } catch {
            return false;
        }
    });
    const setShowArchived = useCallback((value: boolean) => {
        setShowArchivedState(value);
        try {
            localStorage.setItem('workstream.showArchived', String(value));
        } catch {
            // ignore storage errors (private mode, etc.)
        }
    }, []);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    // Mirror tasks state in a ref so handlers can read the latest value
    // synchronously, avoiding stale-closure / deferred-updater races.
    const tasksRef = useRef<Task[]>([]);
    useEffect(() => {
        tasksRef.current = tasks;
    }, [tasks]);
    const [taskErrors, setTaskErrors] = useState<string[]>([]);
    const effectiveWorkstreamID = workstream?.workstreamID ?? workstreamID;

    const pushError = useCallback((msg: string) => {
        setTaskErrors((prev) => [...prev, msg]);
    }, []);

    const dismissError = useCallback((index: number) => {
        setTaskErrors((prev) => prev.filter((_, i) => i !== index));
    }, []);

    // Refetch the full task list from the server. Used when we need to
    // reconcile local state with authoritative server state (e.g. after a
    // reorder failure where rolling back a stale snapshot would clobber
    // other concurrent mutations). Throws on failure so callers can
    // detect when reconciliation was not possible.
    const refetchTasks = useCallback(async () => {
        if (!effectiveWorkstreamID) {
            throw new Error('missing workstream id');
        }
        const baseUrl = `/api/workstreams/${encodeURIComponent(effectiveWorkstreamID)}/tasks?maxResults=200&includeDeleted=true`;
        const all: Task[] = [];
        let url = baseUrl;
        while (url) {
            const resp = await fetch(url);
            if (!resp.ok) {
                throw new Error(`refetch failed: ${resp.status}`);
            }
            const data: ListTasksResponse = await resp.json();
            all.push(...(data.Tasks ?? []));
            if (data.NextToken) {
                url = `${baseUrl}&token=${encodeURIComponent(data.NextToken)}`;
            } else {
                url = '';
            }
        }
        setTasks(all);
    }, [effectiveWorkstreamID]);

    const handleTaskChanged = useCallback((updated: Task) => {
        const existing = tasksRef.current.find((t) => t.TaskID === updated.TaskID);
        const deletedChanged = existing && !!existing.Deleted !== !!updated.Deleted;
        setTasks((prev) => prev.map((t) => (t.TaskID === updated.TaskID ? { ...t, ...updated } : t)));
        if (deletedChanged) {
            refetchTasks().catch(() => {});
        }
    }, [refetchTasks]);

    const handleCreateTask = useCallback(async () => {
        if (!effectiveWorkstreamID) return;
        try {
            const resp = await fetch(`/api/workstreams/${encodeURIComponent(effectiveWorkstreamID)}/tasks`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ Title: 'New Task', Parallel: false }),
            });
            if (!resp.ok) {
                const text = await resp.text();
                setTaskErrors((prev) => [...prev, text || 'Failed to create task']);
                return;
            }
            const newTask: Task = await resp.json();
            // Dedupe by TaskID: if the background pagination loop already
            // appended this task (because it landed in a later page), this
            // optimistic append would otherwise produce a duplicate. Guard
            // the same way in the pagination loop below.
            setTasks((prev) => {
                if (prev.some((t) => t.TaskID === newTask.TaskID)) return prev;
                return [...prev, newTask];
            });
        } catch (err: unknown) {
            setTaskErrors((prev) => [
                ...prev,
                err instanceof Error ? err.message : 'Failed to create task',
            ]);
        }
    }, [effectiveWorkstreamID]);

    const handleReorderTask = useCallback(
        async (
            taskID: string,
            anchor: { kind: 'after' | 'before'; targetTaskID: string },
        ) => {
            if (!effectiveWorkstreamID) return;

            // Read current tasks from the ref so we have a deterministic
            // snapshot to compute the version and new order from. This
            // avoids relying on the setTasks updater running synchronously.
            const snapshot = tasksRef.current;
            const fromIdx = snapshot.findIndex((t) => t.TaskID === taskID);
            const targetIdx = snapshot.findIndex((t) => t.TaskID === anchor.targetTaskID);
            if (fromIdx === -1 || targetIdx === -1) return;
            const version = snapshot[fromIdx].Version ?? 0;

            // Optimistically reorder the local tasks array. On failure we
            // prefer refetching authoritative server state over restoring
            // the snapshot, since concurrent mutations (other reorders,
            // title edits, etc.) may have landed in the interim. The
            // snapshot is kept only as a last-resort fallback for when the
            // refetch itself fails (e.g. total API outage).
            setTasks((prev) => {
                const prevFromIdx = prev.findIndex((t) => t.TaskID === taskID);
                const prevTargetIdx = prev.findIndex((t) => t.TaskID === anchor.targetTaskID);
                if (prevFromIdx === -1 || prevTargetIdx === -1) return prev;
                const next = prev.slice();
                const [moved] = next.splice(prevFromIdx, 1);
                // After splice, target index may shift if prevFromIdx < prevTargetIdx.
                const adjustedTargetIdx = next.findIndex((t) => t.TaskID === anchor.targetTaskID);
                if (adjustedTargetIdx === -1) return prev;
                const insertAt = anchor.kind === 'after' ? adjustedTargetIdx + 1 : adjustedTargetIdx;
                next.splice(insertAt, 0, moved);
                return next;
            });

            const fields: Record<string, unknown> = {};
            if (anchor.kind === 'after') {
                fields.AfterTaskID = anchor.targetTaskID;
            } else {
                fields.BeforeTaskID = anchor.targetTaskID;
            }

            // Recover from a failed reorder by trying to refetch authoritative
            // state; if that also fails, restore the pre-drag snapshot so the
            // board isn't stuck showing an order that was never persisted.
            const recover = async () => {
                try {
                    await refetchTasks();
                } catch {
                    setTasks(snapshot);
                }
            };

            try {
                const resp = await fetch(
                    `/api/workstreams/${encodeURIComponent(effectiveWorkstreamID)}/tasks/${encodeURIComponent(taskID)}`,
                    {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ Version: version, Fields: fields }),
                    },
                );
                if (!resp.ok) {
                    const text = await resp.text();
                    setTaskErrors((prev) => [...prev, text || 'Failed to reorder task']);
                    await recover();
                    return;
                }
                const updated: Task = await resp.json();
                // Merge the updated version field so subsequent edits succeed.
                setTasks((prev) =>
                    prev.map((t) => (t.TaskID === updated.TaskID ? { ...t, ...updated } : t)),
                );
            } catch (err: unknown) {
                setTaskErrors((prev) => [
                    ...prev,
                    err instanceof Error ? err.message : 'Failed to reorder task',
                ]);
                await recover();
            }
        },
        [effectiveWorkstreamID, refetchTasks],
    );

    // Scroll a task card into view (vertically on the page, horizontally
    // within the swim lane grid). Tries a few animation frames because
    // the card may not yet be in the DOM at the exact moment the state
    // update lands.
    const scrollTaskIntoView = useCallback((taskID: string) => {
        let attempts = 0;
        const tryScroll = () => {
            const el = document.querySelector(`[data-task-id="${CSS.escape(taskID)}"]`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
                return;
            }
            attempts += 1;
            if (attempts < 10) {
                requestAnimationFrame(tryScroll);
            }
        };
        requestAnimationFrame(tryScroll);
    }, []);

    const fetchWorkstream = useCallback(async () => {
        if (!effectiveWorkstreamID) return;
        try {
            const resp = await fetch(`/api/workstreams/${encodeURIComponent(effectiveWorkstreamID)}`);
            if (resp.ok) {
                const data: Workstream = await resp.json();
                setWorkstream(data);
            }
        } catch {}
    }, [effectiveWorkstreamID]);

    const handleChangeTaskState = useCallback(
        async (taskID: string, newState: string) => {
            if (!effectiveWorkstreamID) return;

            // Snapshot current tasks so we can restore on total failure.
            const snapshot = tasksRef.current;
            const idx = snapshot.findIndex((t) => t.TaskID === taskID);
            if (idx === -1) return;
            const task = snapshot[idx];
            if ((task.State ?? '') === newState) return;
            const version = task.Version ?? 0;

            // Optimistic local update: change the task's State. Rank is
            // unchanged per spec — "moving a task to a different swimlane
            // doesn't change its ordering."
            setTasks((prev) =>
                prev.map((t) => (t.TaskID === taskID ? { ...t, State: newState } : t)),
            );

            // After the optimistic update lands, scroll the card into
            // view in its new lane.
            scrollTaskIntoView(taskID);

            const recover = async () => {
                try {
                    await refetchTasks();
                } catch {
                    setTasks(snapshot);
                }
            };

            try {
                const resp = await fetch(
                    `/api/workstreams/${encodeURIComponent(effectiveWorkstreamID)}/tasks/${encodeURIComponent(taskID)}`,
                    {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ Version: version, Fields: { State: newState, AutoPause: true } }),
                    },
                );
                if (!resp.ok) {
                    const text = await resp.text();
                    setTaskErrors((prev) => [...prev, text || 'Failed to change task state']);
                    await recover();
                    return;
                }
                const updated: Task = await resp.json();
                setTasks((prev) =>
                    prev.map((t) => (t.TaskID === updated.TaskID ? { ...t, ...updated } : t)),
                );
                // Re-scroll in case the server assigned a slightly
                // different rank/position than our optimistic update
                // implied.
                scrollTaskIntoView(taskID);
                // The backend auto-pauses the workstream when an
                // AI-assigned task's state changes. Refresh the header
                // so the pause indicator updates immediately.
                if (task.AssignedToAI) {
                    fetchWorkstream();
                }
            } catch (err: unknown) {
                setTaskErrors((prev) => [
                    ...prev,
                    err instanceof Error ? err.message : 'Failed to change task state',
                ]);
                await recover();
            }
        },
        [effectiveWorkstreamID, refetchTasks, scrollTaskIntoView, fetchWorkstream],
    );

    useEffect(() => {
        activeIDRef.current = workstreamID;
        if (!workstreamID) {
            setError('Invalid URL');
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(undefined);
        setWorkstream(null);

        fetch(`/api/workstreams/${encodeURIComponent(workstreamID)}`)
            .then(async (resp) => {
                if (cancelled) return;
                if (!resp.ok) {
                    const text = await resp.text();
                    if (cancelled) return;
                    setError(text || 'Failed to load workstream');
                    setLoading(false);
                    return;
                }
                const data: Workstream = await resp.json();
                if (cancelled) return;
                setWorkstream(data);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err.message || 'Failed to load workstream');
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [workstreamID]);

    // Fetch task pages incrementally, rendering as each page arrives.
    // Load tasks when the workstream ID changes. This intentionally does
    // NOT depend on the `workstream` object — only the ID — so that
    // refreshing workstream metadata (e.g. after an AI state change
    // auto-pauses the workstream) does not trigger a full task reload
    // with the associated setTasks([]) flicker.
    useEffect(() => {
        if (!effectiveWorkstreamID) return;
        let cancelled = false;
        setTasks([]);
        setInitialLoadDone(false);
        const baseUrl = `/api/workstreams/${encodeURIComponent(effectiveWorkstreamID)}/tasks?maxResults=200&includeDeleted=true`;
        (async () => {
            let url = baseUrl;
            while (url) {
                const resp = await fetch(url);
                if (cancelled || !resp.ok) return;
                const data: ListTasksResponse = await resp.json();
                const page = data.Tasks ?? [];
                // Dedupe by TaskID: handleCreateTask may have optimistically
                // appended a newly created task that also appears on a later
                // page of the initial load.
                if (!cancelled) {
                    setTasks((prev) => {
                        const existing = new Set(prev.map((t) => t.TaskID));
                        const fresh = page.filter((t) => !existing.has(t.TaskID));
                        if (fresh.length === 0) return prev;
                        return [...prev, ...fresh];
                    });
                }
                if (data.NextToken) {
                    url = `${baseUrl}&token=${encodeURIComponent(data.NextToken)}`;
                } else {
                    url = '';
                }
            }
            if (!cancelled) setInitialLoadDone(true);
        })().catch(() => {});
        return () => { cancelled = true; };
    }, [effectiveWorkstreamID]); // eslint-disable-line react-hooks/exhaustive-deps

    // Poll for task updates — only after initial load completes to
    // avoid racing with mount-time pagination.
    useTaskPolling(effectiveWorkstreamID, tasksRef, setTasks, initialLoadDone);

    const handleSaveTitle = useCallback(async (newName: string) => {
        if (!effectiveWorkstreamID || !workstream) return;
        const resp = await fetch(`/api/workstreams/${encodeURIComponent(effectiveWorkstreamID)}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ Version: workstream.version, Name: newName }),
        });
        if (!resp.ok) {
            const text = await resp.text();
            throw new Error(text || 'Failed to save title');
        }
        const data: Workstream = await resp.json();
        if (activeIDRef.current === workstreamID) {
            setWorkstream(data);
        }
    }, [effectiveWorkstreamID, workstreamID, workstream]);

    const handleSaveDescription = useCallback(async (newDesc: string) => {
        if (!effectiveWorkstreamID || !workstream) return;
        const resp = await fetch(`/api/workstreams/${encodeURIComponent(effectiveWorkstreamID)}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ Version: workstream.version, Description: newDesc }),
        });
        if (!resp.ok) {
            const text = await resp.text();
            throw new Error(text || 'Failed to save description');
        }
        const data: Workstream = await resp.json();
        if (activeIDRef.current === workstreamID) {
            setWorkstream(data);
        }
    }, [effectiveWorkstreamID, workstreamID, workstream]);

    if (loading) {
        return (
            <div style={{ paddingTop: '60px', paddingLeft: '16px', paddingRight: '16px' }}>
                <p>Loading...</p>
            </div>
        );
    }

    if (error || !workstream) {
        return (
            <div style={{ paddingTop: '60px', paddingLeft: '16px', paddingRight: '16px' }}>
                <p style={{ color: 'red' }}>{error || 'Workstream not found'}</p>
            </div>
        );
    }

    return (
        <div style={{ paddingTop: '20px' }}>
            <ErrorBanners errors={taskErrors} onDismiss={dismissError} />
            {workstreamID && (
                <>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 16px',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <WorkstreamBreadcrumb
                                workstreamID={workstreamID}
                                workstream={workstream}
                            />
                            <PermalinkCopyButton
                                url={`${window.location.origin}/workstreams/${encodeURIComponent(workstream.workstreamID)}`}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <WorkstreamActions
                                workstream={workstream}
                                onWorkstreamChanged={fetchWorkstream}
                            />
                        </div>
                    </div>
                    <div style={{ padding: '0 16px' }}>
                        <EditableTitle
                            title={workstream.name}
                            onSave={handleSaveTitle}
                        />
                        <EditableDescription
                            description={workstream.description}
                            onSave={handleSaveDescription}
                        />
                        <TaskSettings
                            workstream={workstream}
                            onWorkstreamChanged={fetchWorkstream}
                        />
                        <ShowArchivedToggle
                            checked={showArchived}
                            onChange={setShowArchived}
                        />
                        <SwimLanes
                            tasks={showArchived ? tasks : tasks.filter((t) => !t.Deleted)}
                            defaultShortName={workstream.defaultShortName}
                            onTaskChanged={handleTaskChanged}
                            onError={pushError}
                            onWorkstreamChanged={fetchWorkstream}
                            onCreateTask={handleCreateTask}
                            onReorderTask={handleReorderTask}
                            onChangeTaskState={handleChangeTaskState}
                        />
                    </div>
                </>
            )}
        </div>
    );
}
